/**
 * Landing V2 — 14-section marketing landing, driven by admin CMS content.
 * Header/Footer/FAQ are imported from existing components and NOT redesigned.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, Check, Sparkles, Star, Quote as QuoteIcon } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PURPLE_CYAN = 'linear-gradient(135deg, #7A00EB 0%, #00c3ef 100%)';
const TEAL = '#00B5B4';

/* ─────────────────── content hook ─────────────────── */

export function useLandingContent() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/seo/landing-content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setContent(d))
      .catch(() => {});
  }, []);
  return content;
}

/* Reusable strap-line (teal, uppercase, tracked) */
const Strap = ({ children, className = '' }) => (
  <p className={`text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase mb-3 ${className}`} style={{ color: TEAL }}>
    {children}
  </p>
);

/* ═══════════════════ 1. HERO ═══════════════════ */
export function HeroV2({ data }) {
  const nav = useNavigate();
  const d = data || {};
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 md:pt-20 pb-0 md:pb-0">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          <div className="lg:col-span-7 text-left pb-16 md:pb-20">
            {d.badge && (
              <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-semibold tracking-[0.14em] uppercase text-purple-700 dark:text-purple-300 px-3 py-1.5 bg-purple-50 dark:bg-purple-500/10 rounded-full mb-5 border border-purple-100 dark:border-purple-500/20">
                <Sparkles className="w-3.5 h-3.5" /> {d.badge}
              </span>
            )}
            {/* Headline in theme gradient */}
            <h1 className="text-[1.55rem] sm:text-[2.15rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold leading-[1.08] tracking-tight"
                style={{ background: PURPLE_CYAN, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {d.headline || 'Is your brand playing too many personalities?'}
            </h1>
            {/* Sub-line in white (light mode: black for contrast) */}
            {d.sub_headline && (
              <p className="mt-4 text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">
                {d.sub_headline}
              </p>
            )}
            {d.body && (
              <p className="mt-5 text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
                {d.body}
              </p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-start">
              <div className="flex flex-col items-center sm:items-start">
                <Button onClick={() => nav('/instructions')} data-testid="hero-start-btn"
                  className="text-white text-base sm:text-lg font-semibold px-7 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all hover:opacity-95 w-full sm:w-auto"
                  style={{ background: PURPLE_CYAN }}>
                  {d.cta_primary_label || "Get your Brand's Personality"} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                {d.cta_primary_note && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{d.cta_primary_note}</p>}
              </div>
              <div className="flex flex-col items-center sm:items-start">
                <a href="/Therefore_Brand_Archetype_Sample_Report.pdf" download data-testid="hero-sample-btn"
                  className="inline-flex items-center justify-center gap-2 text-base font-medium px-6 py-3 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                  <Download className="w-4 h-4" /> {d.cta_secondary_label || 'Download Sample Report'}
                </a>
                {d.cta_secondary_note && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{d.cta_secondary_note}</p>}
              </div>
            </div>
          </div>
          {/* Image column: +30% size, aligned to base */}
          <div className="lg:col-span-5 order-first lg:order-last flex items-end justify-center lg:justify-end">
            <div className="relative w-full max-w-[730px] mx-auto lg:mr-0">
              {d.image_url && (
                <img
                  src={d.image_url}
                  alt={d.image_alt || 'Brand Archetype Quiz illustration'}
                  className="block w-full h-auto"
                  loading="eager"
                  fetchPriority="high"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 2. BREAKER ═══════════════════ */
export function BreakerV2({ data }) {
  const d = data || {};
  return (
    <section className="py-10 md:py-14 text-white" style={{ background: PURPLE_CYAN }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left sm:text-center">
        <h2 className="text-xs sm:text-sm md:text-base font-semibold tracking-[0.2em] uppercase opacity-95">
          {d.headline || "DOES YOUR BRAND LIVE IN PEOPLE'S SUBCONSCIOUS?"}
        </h2>
        <p className="mt-3 text-lg sm:text-xl md:text-2xl font-bold leading-snug max-w-4xl mx-auto">
          {d.sub_headline || '95% of purchase decisions happen in the subconscious — as estimated by Harvard professor Gerald Zaltman.'}
        </p>
      </div>
    </section>
  );
}

/* ═══════════════════ 3. PAIN POINTS ═══════════════════ */
export function PainPointsV2({ data }) {
  const d = data || {};
  const items = d.items || [];
  return (
    <section className="py-16 md:py-24 bg-white dark:bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {d.section_header && (
          <h2 className="text-left sm:text-center text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight mb-10 md:mb-14">
            {d.section_header}
          </h2>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {items.map((it, i) => (
            <div key={i} className="relative rounded-2xl p-[2px] transition-all hover:-translate-y-0.5 hover:shadow-md"
                 style={{ background: PURPLE_CYAN }}>
              <div className="relative h-full bg-gray-50 dark:bg-gray-950 rounded-[14px] p-4 sm:p-7 min-h-[90px] sm:min-h-[180px]">
                {/* Quote mark - Exo 2 Bold, top-right corner */}
                <span aria-hidden
                      className="absolute -top-2 right-3 sm:right-4 text-[50px] sm:text-[80px] leading-none select-none"
                      style={{
                        fontFamily: '"Exo 2", sans-serif',
                        fontWeight: 700,
                        background: PURPLE_CYAN,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        opacity: 0.85,
                      }}>&rdquo;</span>
                <p className="relative mt-2 sm:mt-6 pr-10 text-sm sm:text-lg font-medium text-gray-800 dark:text-gray-200 leading-relaxed">
                  {it.quote}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 4. PROBLEM IDENTIFICATION ═══════════════════ */
export function ProblemIdentificationV2({ data }) {
  const d = data || {};
  const bullets = d.bullets || [];
  return (
    <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-950 border-y border-gray-100 dark:border-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-10 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            {/* Exo 2 Thin, ~100pt on desktop, in theme gradient */}
            <h2 className="leading-[1.05] tracking-tight"
                style={{
                  fontFamily: '"Exo 2", sans-serif',
                  fontWeight: 100,
                  fontSize: 'clamp(2.2rem, 6.5vw, 6.25rem)',
                  background: PURPLE_CYAN,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
              {d.headline_left || "You're not inconsistent."}
              <span className="block mt-1">
                {d.headline_left_2 || "You're undefined."}
              </span>
            </h2>
            {d.sub_headline_left && (
              <p className="mt-5 text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-xl leading-relaxed">
                {d.sub_headline_left}
              </p>
            )}
          </div>
          <div className="md:col-span-3">
            <ul className="space-y-3">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-base font-medium text-gray-600 dark:text-gray-400">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-500 dark:bg-gray-400" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {d.bottom_line && (
              <p className="mt-6 text-base sm:text-lg font-semibold leading-snug" style={{ color: TEAL }}>
                {d.bottom_line}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 5. PROOF 1 — 12 archetype cards (auto-flip loop + hover/tap) ═══════════════════ */
function ArchetypeFlipCard({ archetype, isAutoFlipped, onUserInteract }) {
  const [userFlipped, setUserFlipped] = useState(false);
  const flipped = userFlipped || isAutoFlipped;
  return (
    <button
      type="button"
      onClick={() => { setUserFlipped((v) => !v); onUserInteract?.(); }}
      onMouseEnter={() => { setUserFlipped(true); onUserInteract?.(); }}
      onMouseLeave={() => setUserFlipped(false)}
      data-testid={`archetype-flip-${archetype.name.toLowerCase()}`}
      className="archetype-card group relative rounded-xl p-[2px] transition-shadow text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
      style={{ background: PURPLE_CYAN, perspective: '800px' }}
    >
      <div
        className="relative w-full aspect-[3/3.5] transition-transform duration-500"
        style={{ transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
      >
        {/* Front — icon + name (BLACK interior; gradient border via parent) */}
        <div
          className="absolute inset-0 bg-[#000000] rounded-[10px] px-3 py-3 sm:px-4 sm:py-4 flex flex-col items-center justify-between gap-1"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          <img
            src={archetype.icon}
            alt={`${archetype.name} archetype`}
            className="h-[85%] w-auto max-w-full object-contain"
            loading="lazy"
          />
          <div className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#ffffff] text-center">
            {archetype.name}
          </div>
        </div>
        {/* Back — brand logo on WHITE */}
        <div
          className="absolute inset-0 bg-[#ffffff] rounded-[10px] px-3 py-4 flex items-center justify-center"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          {archetype.logo ? (
            <img
              src={archetype.logo}
              alt={`Example ${archetype.name} brand logo`}
              className="max-w-[80%] max-h-[70%] object-contain"
              loading="lazy"
            />
          ) : (
            <span className="text-xs text-gray-400">Example brand</span>
          )}
        </div>
      </div>
    </button>
  );
}

export function Proof1V2({ data }) {
  const d = data || {};
  const archetypes = d.archetypes || [];
  const gridRef = React.useRef(null);
  const [autoIdx, setAutoIdx] = useState(-1);       // -1 = no card auto-flipped
  const [autoActive, setAutoActive] = useState(false); // starts once scrolled into view
  const [userTouched, setUserTouched] = useState(false); // permanently pause after any interaction? No — pause briefly

  // Respect prefers-reduced-motion
  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Start auto-flip once the grid scrolls into view
  useEffect(() => {
    if (reducedMotion || !gridRef.current) return;
    const el = gridRef.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setAutoActive(true);
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -10% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [reducedMotion]);

  // Sequential cycle: 0 → 1 → 2 … → 11 → -1 (all reset) → 0 → …
  useEffect(() => {
    if (!autoActive || archetypes.length === 0) return;
    let i = 0;
    setAutoIdx(0);
    const id = setInterval(() => {
      i = i + 1;
      // Insert a brief "all reset" frame between cycles so the last card flips back
      if (i > archetypes.length - 1) {
        setAutoIdx(-1);
        setTimeout(() => { i = 0; setAutoIdx(0); }, 600);
        return;
      }
      setAutoIdx(i);
    }, 3200); // ~3.2s per card
    return () => clearInterval(id);
  }, [autoActive, archetypes.length]);

  // If user interacts, pause the auto-loop for 8s then resume
  const pauseAutoLoop = React.useCallback(() => {
    setUserTouched(true);
    setAutoActive(false);
    const t = setTimeout(() => { setUserTouched(false); setAutoActive(true); }, 8000);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="py-16 md:py-24 bg-white dark:bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-left sm:text-center max-w-3xl mx-auto mb-12">
          {d.strap && <Strap>{d.strap}</Strap>}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
            {d.headline || 'They behave like someone. They have a Personality.'}
          </h2>
          {d.body && (
            <p className="mt-5 text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {d.body}
            </p>
          )}
        </div>
        <div ref={gridRef} className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4 md:gap-5">
          {archetypes.map((a, i) => (
            <ArchetypeFlipCard
              key={a.name}
              archetype={a}
              isAutoFlipped={!userTouched && i === autoIdx}
              onUserInteract={pauseAutoLoop}
            />
          ))}
        </div>
        {d.disclaimer && (
          <p className="mt-10 md:mt-12 max-w-4xl mx-auto text-left sm:text-center text-[11px] sm:text-xs text-gray-400 dark:text-gray-600 italic leading-relaxed">
            {d.disclaimer}
          </p>
        )}
      </div>
    </section>
  );
}

/* ═══════════════════ 6. PROOF 2 — Jung split ═══════════════════ */
export function Proof2V2({ data }) {
  const d = data || {};
  return (
    <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-950 border-y border-gray-100 dark:border-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-10 gap-10 md:gap-14 items-center">
          <div className="md:col-span-3">
            {d.image_url && (
              <div className="relative max-w-[280px] mx-auto md:mx-0">
                <img src={d.image_url} alt={d.image_alt || 'Carl Jung'} loading="lazy"
                     className="w-full h-auto rounded-2xl shadow-xl grayscale-[15%]" />
                <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-600 italic text-center">
                  {d.image_credit || 'Carl Jung, 1935. Image: Wikimedia Commons, public domain.'}
                </p>
              </div>
            )}
          </div>
          <div className="md:col-span-7">
            {d.strap && <Strap>{d.strap}</Strap>}
            <h2 className="text-2xl sm:text-3xl md:text-[2.2rem] font-bold text-gray-900 dark:text-white leading-tight mb-5">
              {d.headline || 'Based on a 100-year-old framework'}
            </h2>
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
              {d.body}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 7 / 10 / 13. CTA (reusable) ═══════════════════ */
export function CtaBlockV2({ data, testId = 'cta-block', compact = false }) {
  const nav = useNavigate();
  const d = data || {};
  const py = compact ? 'py-10 md:py-14' : 'py-16 md:py-24';
  return (
    <section className={`${py} bg-white dark:bg-black text-left sm:text-center`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {d.strap && <Strap className="justify-center">{d.strap}</Strap>}
        {d.headline && (
          <h2 className="text-2xl sm:text-3xl md:text-[2.4rem] font-extrabold leading-tight text-gray-900 dark:text-white">
            {d.headline}
          </h2>
        )}
        {d.sub_headline && (
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {d.sub_headline}
          </p>
        )}
        {d.tagline && (
          <p className="mt-4 text-sm sm:text-base font-bold tracking-[0.14em] uppercase"
             style={{ background: PURPLE_CYAN, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {d.tagline}
          </p>
        )}
        {d.cta_label && (
          <div className={`${d.tagline ? 'mt-4' : 'mt-8'} flex flex-col items-center gap-2`}>
            <Button onClick={() => nav('/instructions')} data-testid={testId}
              className="text-white text-base sm:text-lg font-semibold px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all hover:opacity-95"
              style={{ background: PURPLE_CYAN }}>
              {d.cta_label} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {d.cta_note && <p className="text-xs text-gray-500 dark:text-gray-400">{d.cta_note}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

function SneakItem({ item, iconSrc }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-12 h-12">
        {iconSrc && (
          <img src={iconSrc} alt="" className="w-12 h-12" />
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white">
          {item.title}
        </h3>
        <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed" style={{ textWrap: 'pretty' }}>
          {item.highlight ? (
            <>
              {item.body}&nbsp;
              <span style={{ color: '#2cb5ef', fontWeight: 500 }}>{item.highlight}</span>
            </>
          ) : (
            item.body
          )}
        </p>
      </div>
    </div>
  );
}

function TierGroup({ tier, children }) {
  return (
    <div className="relative rounded-2xl p-[2px] mt-6" style={{ background: PURPLE_CYAN }}>
      <div className="bg-gray-50 dark:bg-gray-950 rounded-[14px] px-5 pt-8 pb-5 sm:px-6 sm:pt-9 sm:pb-6">
        <span className="absolute -top-3 left-6 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-white px-3 py-1 rounded-full"
              style={{ background: PURPLE_CYAN }}>
          {tier === 'FREE' ? 'FREE TIER' : 'PREMIUM TIER'}
        </span>
        <div className="space-y-5">{children}</div>
      </div>
    </div>
  );
}

/* ═══════════════════ 8. SNEAK PEEK ═══════════════════ */
export function SneakPeekV2({ data }) {
  const d = data || {};
  const items = d.items || [];
  const iconMap = ['/landing/report_gives_icons-01.svg', '/landing/report_gives_icons-02.svg', '/landing/report_gives_icons-03.svg', '/landing/report_gives_icons-04.svg'];

  // Group items by tier — one FREE group, one PREMIUM group
  const withIcons = items.map((it, i) => ({ ...it, _icon: it.icon || iconMap[i] || null }));
  const freeItems = withIcons.filter((it) => it.tier === 'FREE');
  const premiumItems = withIcons.filter((it) => it.tier === 'PREMIUM');

  return (
    <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-950 border-y border-gray-100 dark:border-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="order-2 md:order-1">
            {d.image_url && (
              <img src={d.image_url} alt={d.image_alt || 'Sample report pages'}
                   loading="lazy" className="w-full h-auto rounded-xl shadow-2xl" />
            )}
          </div>
          <div className="order-1 md:order-2">
            {d.strap && <Strap>{d.strap}</Strap>}
            <h2 className="text-2xl sm:text-3xl md:text-[2rem] font-bold text-gray-900 dark:text-white leading-tight">
              {d.headline || 'Everything a brand strategist would normally build from scratch. Delivered instantly.'}
            </h2>

            {freeItems.length > 0 && (
              <TierGroup tier="FREE">
                {freeItems.map((it, i) => (
                  <SneakItem key={`f-${i}`} item={it} iconSrc={it._icon} />
                ))}
              </TierGroup>
            )}
            {premiumItems.length > 0 && (
              <TierGroup tier="PREMIUM">
                {premiumItems.map((it, i) => (
                  <React.Fragment key={`p-${i}`}>
                    {i > 0 && <div className="border-t border-gray-200 dark:border-gray-800" />}
                    <SneakItem item={it} iconSrc={it._icon} />
                  </React.Fragment>
                ))}
              </TierGroup>
            )}

            {d.cta_label && (
              <div className="mt-10 flex flex-col items-center gap-2">
                <a href="/Therefore_Brand_Archetype_Sample_Report.pdf" download data-testid="sneak-peek-download-btn"
                   className="inline-flex items-center justify-center gap-2 text-base sm:text-lg font-semibold px-8 py-4 rounded-xl text-white shadow-lg hover:shadow-xl transition-all hover:opacity-95"
                   style={{ background: PURPLE_CYAN }}>
                  <Download className="w-5 h-5" /> {d.cta_label}
                </a>
                {d.cta_note && <p className="text-xs text-gray-500 dark:text-gray-400">{d.cta_note}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 9. PROMISE — transformation image ═══════════════════ */
export function PromiseV2({ data }) {
  const d = data || {};
  return (
    <section className="pt-16 md:pt-24 pb-4 md:pb-6 bg-white dark:bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left sm:text-center">
        {/* CLARITY STARTS HERE. — Exo 2 Thin in theme gradient (matches Section 4 style) */}
        <h2 className="leading-[1.05] tracking-tight"
            style={{
              fontFamily: '"Exo 2", sans-serif',
              fontWeight: 100,
              fontSize: 'clamp(2.2rem, 6.5vw, 6.25rem)',
              background: PURPLE_CYAN,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
          {d.headline || 'CLARITY STARTS HERE.'}
        </h2>
        {d.sub_headline && (
          <p className="mt-4 text-lg sm:text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100">
            {d.sub_headline}
          </p>
        )}
        <div className="mt-8 md:mt-10 -mb-4">
          {d.image_web_url && (
            <img src={d.image_web_url} alt={d.image_alt || 'From chaos to clarity'}
                 loading="lazy" className="hidden md:block w-full h-auto max-w-5xl mx-auto rounded-xl" />
          )}
          {d.image_mobile_url && (
            <img src={d.image_mobile_url} alt={d.image_alt || 'From chaos to clarity'}
                 loading="lazy" className="block md:hidden w-full h-auto rounded-xl" />
          )}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 11. TESTIMONIALS ═══════════════════ */
export function TestimonialsV2() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/testimonials/approved`)
      .then((r) => (r.ok ? r.json() : { testimonials: [] }))
      .then((d) => setItems(d.testimonials || []))
      .catch(() => setItems([]));
  }, []);
  if (items.length < 3) return null;

  return (
    <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-950 border-y border-gray-100 dark:border-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-left sm:text-center text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-12">
          Hear what our founders have to say.
        </h2>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {items.slice(0, 6).map((t) => (
            <div key={t.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 sm:p-7 flex flex-col">
              <QuoteIcon className="w-7 h-7 mb-3" style={{ color: '#7A00EB' }} />
              <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 leading-relaxed flex-1">{t.text}</p>
              <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: t.rating || 5 }).map((_, i) => (
                    <Star key={`s-${i}`} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div className="font-semibold text-sm text-gray-900 dark:text-white">{t.name}</div>
                {t.company && <div className="text-xs text-gray-500 dark:text-gray-400">{t.company}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────── testimonial aggregate for schema ─────────────────── */
export function useTestimonialsAggregate() {
  const [agg, setAgg] = useState({ count: 0, avg: 0 });
  useEffect(() => {
    fetch(`${API_URL}/api/testimonials/approved`)
      .then((r) => (r.ok ? r.json() : { testimonials: [] }))
      .then((d) => {
        const arr = d.testimonials || [];
        if (arr.length === 0) return;
        const sum = arr.reduce((s, t) => s + (t.rating || 5), 0);
        setAgg({ count: arr.length, avg: (sum / arr.length).toFixed(1) });
      })
      .catch(() => {});
  }, []);
  return agg;
}
